import React, { useMemo, useState } from 'react'
import { toast } from 'sonner'
import HttpClient from '../../../../services/HttpClient'
import {
  STAT_KEYS,
  emptyStats,
  sumStats,
} from '../../shared/statAllocation'
import '../../shared/StatAllocationPanel.css'

const EN_BY_ES = {
  fuerza: 'strength',
  destreza: 'dexterity',
  agilidad: 'agility',
  resistencia: 'resistance',
  voluntad: 'willpower',
  controlChakra: 'chakraControl',
  concentracion: 'concentration',
  inteligencia: 'intelligence',
}

/**
 * Reparto de puntos pendientes por level-up (Genin+).
 * Solo incrementa sobre la base actual; se puede gastar parcial.
 */
export default function LevelUpStatPointsPanel({
  slug,
  unspent = 0,
  currentStats = {},
  onComplete,
}) {
  const [adds, setAdds] = useState(() => emptyStats())
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  const used = useMemo(() => sumStats(adds), [adds])
  const left = Math.max(0, Number(unspent) || 0) - used

  const setAdd = (key, raw) => {
    const n = Math.max(0, parseInt(raw, 10) || 0)
    setAdds((cur) => {
      const next = { ...cur, [key]: n }
      const total = sumStats(next)
      if (total > Number(unspent)) return cur
      return next
    })
  }

  const submit = async () => {
    if (used <= 0) {
      setError('Asigna al menos 1 punto.')
      return
    }
    if (used > Number(unspent)) {
      setError('No puedes gastar más puntos de los que tienes.')
      return
    }
    setBusy(true)
    setError(null)
    try {
      const res = await HttpClient().post(`/api/v1/characters/${slug}/stats/spend`, {
        allocations: adds,
      })
      if (res?.success !== true) {
        throw new Error(res?.message || 'No se pudo guardar')
      }
      toast.success(
        res?.data?.unspentStatPoints > 0
          ? `Atributos actualizados · quedan ${res.data.unspentStatPoints} puntos`
          : 'Atributos actualizados'
      )
      setAdds(emptyStats())
      onComplete?.(res?.data)
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Error al asignar')
    } finally {
      setBusy(false)
    }
  }

  if (!unspent || unspent <= 0) return null

  return (
    <article className="char-card char-card--section level-up-stats">
      <header className="char-section-header">
        <span className="char-section-header__ornament" aria-hidden="true" />
        <h3 className="char-section-header__title">Puntos por nivel</h3>
        <span className="char-section-header__ornament char-section-header__ornament--right" aria-hidden="true" />
      </header>

      <div className="stat-alloc">
        <p className="stat-alloc__sub">
          Has subido de nivel: reparte hasta <strong>{unspent}</strong> punto
          {unspent === 1 ? '' : 's'} entre tus atributos (puedes guardar solo una parte).
        </p>

        <div className={`stat-alloc__points${left === 0 ? ' stat-alloc__points--ok' : ''}`}>
          <span>
            Pendientes: <strong>{unspent}</strong>
          </span>
          <span>
            En este reparto: <strong>{used}</strong>
          </span>
          <span>
            Quedan libres: <strong>{left}</strong>
          </span>
        </div>

        {error ? (
          <ul className="stat-alloc__errors" role="alert">
            <li>{error}</li>
          </ul>
        ) : null}

        <div className="stat-alloc__rows">
          {STAT_KEYS.map(({ key, label }) => {
            const en = EN_BY_ES[key]
            const base = Number(currentStats?.[en]?.base ?? currentStats?.[en] ?? 0)
            const add = adds[key] || 0
            return (
              <div key={key} className="stat-alloc__row">
                <div className="stat-alloc__label">
                  <strong>{label}</strong>
                  <span className="stat-alloc__breakdown">
                    Actual {base}
                    {add > 0 ? ` + ${add} → ${base + add}` : ''}
                  </span>
                </div>
                <input
                  type="number"
                  min={0}
                  max={unspent}
                  value={add}
                  disabled={busy}
                  onChange={(e) => setAdd(key, e.target.value)}
                  aria-label={`Puntos a ${label}`}
                />
              </div>
            )
          })}
        </div>

        <div className="stat-alloc__footer">
          <button
            type="button"
            className="stat-alloc__submit"
            disabled={busy || used <= 0}
            onClick={submit}
          >
            {busy ? 'Guardando…' : 'Aplicar puntos'}
          </button>
        </div>
      </div>
    </article>
  )
}
